import { handleError } from "@/utils/handleError";
import { AccountApiClient } from "../clients/accountApiClient";
import { BankInfoApiClient } from "../clients/bankInfoApiClient";
import { AccountType, BankAccountSummary, ConnectAccountResult, RelinkAccountsResult, WaitingSettlementsSummary } from "@/types/accounts";
import { translateOutsideReact } from '@/i18n/translateOutsideReact';

export class AccountService {
  /**
   * Connect to a bank account using existing consent
   * @param accountId The ID of the account to connect
   * @param bic The BIC of the bank
   * @returns Promise with connection result
   */
  async connectAccount(accountId: string, bic: string, ref?: string, countryCode?: string, accountType?: AccountType): Promise<ConnectAccountResult> {
    try {
      console.log(`Connecting account ${accountId} with BIC ${bic}`);

      // Call the edge function to connect the account
      const result = await AccountApiClient.connectAccount(accountId, bic, ref, countryCode, accountType);
      console.log("Connected via Edge Function:", { accountId: result.accountId, maskedNumber: result.maskedNumber });
      return result;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToConnectBankAccount'), "connectAccount");
      throw error;
    }
  }

  /**
   * After "Update access" (FIN-863): move this bank's connected accounts onto its newest consent.
   */
  async relinkAccountsToLatestConsent(bic: string, ref?: string, countryCode?: string): Promise<RelinkAccountsResult> {
    return AccountApiClient.relinkAccountsToLatestConsent(bic, ref, countryCode);
  }

  /**
   * Get all bank accounts with active consents. Rethrows on failure (including CONSENT_REQUIRED),
   * never returns [] for one: an empty list means the company has no accounts.
   */
  async getBankAccounts(): Promise<BankAccountSummary[]> {
    const response = await AccountApiClient.getBankAccounts();
    if (!response || !response.accounts) {
      throw new Error("No accounts returned from account-api");
    }
    return response.accounts;
  }

  /**
   * Get available bank accounts to connect for a specific BIC
   * @param bic Bank BIC code
   * @returns Promise<BankAccountSummary[]> or throws CONSENT_REQUIRED error
   */
  async getBankAccountsToConnect(bic: string, ref?: string, countryCode?: string, accountType?: AccountType): Promise<BankAccountSummary[]> {
    try {
      console.log(`Fetching bank accounts to connect for BIC: ${bic}`);
      const response = await AccountApiClient.getBankAccountsToConnect(bic, ref, countryCode, accountType);
      
      if ('accounts' in response) {
        console.log("Received available accounts to connect:", response.accounts);
        return response.accounts;
      }
      
      // Fallback to empty array
      return [];
    } catch (error) {
      console.error("Failed to get bank accounts to connect for BIC:", error);
      
      // Re-throw CONSENT_REQUIRED errors
      if (error.message === 'CONSENT_REQUIRED' || error.error === 'CONSENT_REQUIRED') {
        throw error;
      }
      
      throw error;
    }
  }

  /**
   * Get bank accounts for a specific BIC with consent checking
   * @param bic Bank BIC code
   * @returns Promise<BankAccountSummary[]> or throws CONSENT_REQUIRED error
   */
  async getBankAccountsForBic(bic: string): Promise<BankAccountSummary[]> {
    try {
      console.log(`Fetching bank accounts for BIC: ${bic}`);
      const response = await AccountApiClient.getBankAccounts(bic);
      
      if ('accounts' in response) {
        console.log("Received accounts from Edge Function:", response.accounts);
        return response.accounts;
      }
      
      // Fallback to empty array
      return [];
    } catch (error) {
      console.error("Failed to get bank accounts for BIC:", error);
      
      // Re-throw CONSENT_REQUIRED errors
      if (error.message === 'CONSENT_REQUIRED' || error.error === 'CONSENT_REQUIRED') {
        throw error;
      }
      
      throw error;
    }
  }

  /**
   * Get the total bank balance in the specified currency
   * @param targetCurrency The currency to convert all balances to (default: 'USD')
   * @returns Object with balance and currency
   */
  async getBankBalance(targetCurrency: string = 'USD'): Promise<{ balance: number, currency: string }> {
    return await AccountApiClient.getBankBalance(targetCurrency);
  }

  /**
   * Get list of supported banks
   * @param countryCode Optional country code (defaults to 'SE')
   * @returns Array of supported banks
   */
  async getSupportedBanks(countryCode?: string): Promise<Array<{
    bicFi: string;
    name: string;
    logoUrl: string;
    countryCode: string;
  }>> {
    const response = await BankInfoApiClient.getBanks(countryCode);
    return response.banks;
  }

  /**
   * Multi Bank Account (FIN-90) PR4, F4/AC13: change a bank account's kontoplan mapping.
   * `adoptLedgerHistory` only after the consultant confirmed continuing the target's existing
   * ledger (FIN-968 G1).
   */
  async changeChartMapping(bankAccountId: string, newAccountNumber: string, adoptLedgerHistory = false): Promise<{ chartAccountNumber: string }> {
    return AccountApiClient.changeChartMapping(bankAccountId, newAccountNumber, adoptLedgerHistory);
  }

  /**
   * AC23/R-SWAP-1: swap two of this company's accounts' mapped numbers atomically, given the
   * numbers the consultant saw on each (a stale swap is refused, FIN-968).
   */
  async swapChartMappings(bankAccountIdA: string, bankAccountIdB: string, expectedNumberA: string, expectedNumberB: string): Promise<
    { swapped: true } | { swapped: false; lockedAccountId: string; journalEntryItemCount: number }
  > {
    return AccountApiClient.swapChartMappings(bankAccountIdA, bankAccountIdB, expectedNumberA, expectedNumberB);
  }

  /** FIN-1021: every settlement waiting on a bank account mapping, for the /bank page banner. */
  async getWaitingSettlements(): Promise<WaitingSettlementsSummary> {
    return AccountApiClient.getWaitingSettlements();
  }

  /** FIN-1021/AC6: "Retry booking" for a mapped account whose entries never got sent for booking. */
  async retryBooking(bankAccountId: string): Promise<{ succeeded: boolean }> {
    return AccountApiClient.retryBooking(bankAccountId);
  }
}
